<template>
  <div>
    <div class="chart-display">
      <NoData v-if="errorMsg">{{ errorMsg }}</NoData>
      <VueApexCharts
        width="100%"
        height="100%"
        type="area"
        :options="chartOptions"
        :series="series"
        v-if="forceRerender"
      ></VueApexCharts>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import COLORS from '../../constants/color.js';
import NoData from '../ui/NoData';
import VueApexCharts from 'vue-apexcharts';
import formatNumber from '../../filters/formatNumber.js';
import mixin from '../../utils/mixin';

export default {
  mixins: [mixin],
  props: ['comparedWith'],
  components: {
    NoData,
    VueApexCharts
  },
  filters: {
    formatNumber
  },
  data() {
    return {
      yMax: 100,
      errorMsg: null,
      forceRerender: true, // use to change chart id
      chartOptions: {
        chart: {
          id: 'Comparison_chart',
          animations: {
            // Disable animations in report mode.
            enabled: process.env.VUE_APP_REPORT_MODE ? false : true,
            easing: 'easeinout',
            speed: 800,
            animateGradually: {
              enabled: true,
              delay: 150
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        tooltip: {
          x: {
            show: true,
            format: 'dd MMM yyyy'
          }
        },
        xaxis: {
          type: 'datetime'
        },
        yaxis: {
          title: {
            text: 'Number of hits'
          },
          max: this.yMax,
          labels: {
            formatter: (value) => {
              return formatNumber(value);
            }
          }
        }
      }
    };
  },
  computed: {
    ...mapGetters('dataStore', ['selectedASN']),
    series() {
      if (this.economyData && this.asnData) {
        this.errorMsg = null;
        let dataObj = { name: '...', data: [] };
        if (this.comparedWith === 'economy') {
          dataObj = {
            name: this.getNameByType('economy'),
            data: this.economyData
          };
        } else if (this.comparedWith === 'region') {
          dataObj = {
            name: this.getNameByType('region'),
            data: this.regionData
          };
        } else {
          return [{ name: '...', data: [] }];
        }

        let max = 0;
        // find y max, also, check if all data are 0
        dataObj.data.forEach((i) => {
          if (i.y > max) {
            max = i.y;
          }
        });
        this.asnData.forEach((i) => {
          if (i.y > max) {
            max = i.y;
          }
        });
        const asnName = this.getNameByType('asn');
        this.yMax = max;
        this.updateColors(dataObj);
        return [
          dataObj,
          {
            name: asnName,
            data: this.asnData
          }
        ];
      } else {
        return [{ name: '...', data: [] }];
      }
    }
  },
  methods: {
    updateColors(dataObj) {
      const color = COLORS[dataObj.name];
      let colors;
      if (color) {
        colors = [color, '#0075cf'];
      } else {
        colors = ['#66DA26', '#2E93fA'];
      }
      this.chartOptions = {
        ...this.chartOptions,
        ...{
          colors,
          chart: {
            id: `comparison_AS${this.selectedASN.id}_${dataObj.name}_latest${
              dataObj.data.length > 40 ? '60' : '30'
            }days`,
            animations: {
              // Disable animations in report mode.
              enabled: process.env.VUE_APP_REPORT_MODE ? false : true
            }
          }
        }
      };
      this.forceRerender = false;
      this.$nextTick().then(() => {
        this.forceRerender = true;
      });
    }
  }
};
</script>

<style scoped lang="scss">
.chart-subtitle {
  margin: 0 5px;
  border-bottom: 1px solid $gray-light;
}

.chart-selection {
  display: block !important;
  border-bottom: 1px solid $gray-light;
  margin: 5px;

  span {
    user-select: none;
  }
}

.chart-display {
  flex: 1;
  height: 450px;
  position: relative;
}

::v-deep .apexcharts-menu.open {
  min-width: 120px;
  text-align: center;
}

::v-deep .apexcharts-toolbar {
  z-index: 0;
}
</style>
